import React, { useState } from 'react';
import './Tabs.css';

export interface Tab {
  id: string;
  label: string;
  content: React.ReactNode;
  disabled?: boolean;
}

export interface TabsProps {
  /** Array of tabs */
  tabs: Tab[];
  /** Default active tab ID */
  defaultActiveId?: string;
  /** Controlled active tab ID */
  activeId?: string;
  /** Callback when tab changes */
  onTabChange?: (id: string) => void;
  /** Additional CSS classes */
  className?: string;
  /** Optional content rendered beside the tab list */
  actions?: React.ReactNode;
}

export const Tabs: React.FC<TabsProps> = ({
  tabs,
  defaultActiveId,
  activeId: controlledActiveId,
  onTabChange,
  className = '',
  actions,
}) => {
  const [internalActiveId, setInternalActiveId] = useState(
    defaultActiveId || tabs[0]?.id || ''
  );

  const isControlled = controlledActiveId !== undefined;
  const activeId = isControlled ? controlledActiveId : internalActiveId;

  const handleTabClick = (id: string) => {
    if (!isControlled) {
      setInternalActiveId(id);
    }
    onTabChange?.(id);
  };

  const activeTab = tabs.find((tab) => tab.id === activeId);

  return (
    <div className={`ui-tabs ${className}`.trim()}>
      <div className="ui-tabs__header">
        <div className="ui-tabs__list" role="tablist">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              role="tab"
              aria-selected={tab.id === activeId}
              aria-controls={`panel-${tab.id}`}
              id={`tab-${tab.id}`}
              className={`ui-tabs__tab ${
                tab.id === activeId ? 'ui-tabs__tab--active' : ''
              }`}
              onClick={() => handleTabClick(tab.id)}
              disabled={tab.disabled}
            >
              {tab.label}
            </button>
          ))}
        </div>
        {actions ? <div className="ui-tabs__actions">{actions}</div> : null}
      </div>
      <div
        role="tabpanel"
        id={`panel-${activeId}`}
        aria-labelledby={`tab-${activeId}`}
        className="ui-tabs__panel"
      >
        {activeTab?.content}
      </div>
    </div>
  );
};

Tabs.displayName = 'Tabs';
